import { useAppDispatch, useAppSelector } from '@/store'
import {
  resetProductCsvFile,
  resetRuleSet,
  setProductCsvFile,
} from '@/store/slices/salesTargetSlice'
import { useCallback, useEffect } from 'react'

// Organisms
import { CategoryWrapper, PageContainer, ProductsTable } from '@/components/organisms'
import { FileUploadWithoutModal } from '@/components/organisms/FileUploadWithoutModal'

// Molecules
import { InfoOrError, SectionTitle } from '@/components/molecules'

import ToasterAlert from '@/components/atoms/Toast/toasterAlert'
// Atoms
import { RadioButton, Tag, Toggle } from '@/components/atoms'

// Constants
import { TAG_COLOR } from '@/utils/constants/colors'
import { Constants, NUMBERS } from '@/utils/constants/commonConstants'

// Utils
import { uploadFileDescriptionForCSV } from '@/utils/commonTemplate'

// Helpers
import { useProductList } from '@/containers/ProductsContainer/SalesTargetProducts/useProductList'
import { getCellDataMappedCols } from '@/helpers/dataTableCellDataMapper'
import { downloadCSVFiles, getDateWithTimeString } from '@/helpers/shadcn/utils'
import { downloadTemplateFile, TEMPLATE_TYPE } from '@/services/downloadTemplateFile'
import { FONT_SIZE } from '@/utils/constants/globalStyle'
import { TARGET_TYPES } from '@/utils/constants/saleTargetConstants'

// Define the base cell data mapper object without the "includeExclude" key as it references functions defined later.
const cellDataMapperObj = {
  productName: ({ row: { original } = {} }) => {
    return <div>{original?.productName}</div>
  },
  productId: ({ row: { original } = {} }) => {
    return <div>{original?.productId}</div>
  },
  size: ({ row: { original } = {} }) => {
    return <div>{original?.size}</div>
  },
  status: ({ row: { original } = {} }) => {
    return <div>{original?.status}</div>
  },
  category: ({ row: { original } = {} }) => {
    return <div>{original?.category}</div>
  },
  brand: ({ row: { original } = {} }) => {
    return <div>{original?.brand}</div>
  },
}

const SalesTargetProducts = props => {
  const {
    setSelectedType,
    selectedType,
    uploadComplete,
    onUpdate,
    uploadError,
    setUploadError,
    selectedCategory,
    setSelectedCategory,
    ProductListColConfig,
    setRowSelection,
    rowSelection,
    handleBulkAction,
    categoryListData,
    targetType,
    isAllCategories,
    localProductData,
    setLocalProductData,
    ignoreSkuList,
    setIgnoreSku,
    name,
    showToast,
    setShowToast,
    toastMessage,
    handleIgnoreSkuChange,
    selectedCategoryInitialState
  } = props

  const dispatch = useAppDispatch()
  const { setPageIndex, setSortingObj, setSearchArry, productListData, pageIndex, isLoading } = useProductList()

  // Store included/excluded product state here.


  // Log the product include/exclude map when it changes
  // useEffect(() => {
  //   console.log('Product include/exclude map updated:', productIncludeMap)
  // }, [productIncludeMap])

  // "localProductData" holds the table rows with an "included" key.

  // Pull in includedProducts (or excludedProducts) from Redux
  const includedProducts = useAppSelector(state => { return state.salesTarget?.tabWiseData?.[name]?.includedProducts })
  const selectedRowIds = useAppSelector(state => state.salesTarget?.tabWiseData?.[name]?.selectedRows)
  const mediaFile = useAppSelector(state => state.salesTarget?.tabWiseData?.[name]?.mediaFile)
 
  const getEffectiveSelectedRows = useCallback(() => {
    if (!productListData?.data?.length) return [];
    const selectedProducts = productListData.data.filter(product => 
      (selectedRowIds?.includes(product.productId) || includedProducts?.includes(product.productId))
  );
  return selectedProducts;
}, [productListData?.data, selectedRowIds, includedProducts]);
  
  useEffect(() => {
    if (productListData?.data?.length) {
      // Rebuild local data to reflect updated includes from Redux
      const dataWithIncluded = productListData.data.map(product => ({
        ...product,
        included: includedProducts?.includes(product.productId),
      }))

      setLocalProductData(dataWithIncluded)
    } else {
      setLocalProductData([])
    }
  }, [
    productListData,
    includedProducts, // <-- add these dependencies
    setLocalProductData,
    ignoreSkuList,
    setIgnoreSku,
    showToast
  ])


  // This cell mapper uses the "included" key from local data.
  const localCellDataMapperObjLocal = {
    ...cellDataMapperObj,
    includeExclude: ({ row: { original } = {} }) => {
      const included = original?.included
      return (
        <Tag
          text={included ? 'Exclude' : 'Include'}
          color={included ? TAG_COLOR.RED_150 : TAG_COLOR.GREEN_150}
        />
      )
    },
  }

  const cellDataMappedColsLocal = getCellDataMappedCols(localCellDataMapperObjLocal, ProductListColConfig)

  const handleDelete = () => {
    dispatch(resetProductCsvFile({ activeTab: name }))
    dispatch(resetRuleSet())
  }
  const handleResetAll = () => {
    dispatch(resetRuleSet())
  }

  const handleFileDownload = async () => {
    const body = { type: TEMPLATE_TYPE.PRODUCT_TEMPLATE }
    const response = await downloadTemplateFile(body)
    if (!response) return
    const fileName = `SalesTargetProductTemplate_${getDateWithTimeString()}`
    downloadCSVFiles(response, fileName)
  }

  // useLayoutEffect(()=>[
  //   setSelectedCategory(categoryListData)
  // ], [setSelectedCategory , categoryListData])
  return (
    <>
      <PageContainer>
        <div className="sales-target-component gray850 text-white">
          <div className="flex items-center justify-between">
            <SectionTitle fontSize={FONT_SIZE.XXL} label="Products" />
          </div>
          {showToast &&
            <ToasterAlert type='warning' message={toastMessage} show={true} onCloseClick={() => setShowToast(!showToast)} />
          }
          <div className="radio-wrap mt-4 flex  justify-between flex-row">
            <RadioButton
              key="rd"
              isDark={true}
              items={['Category & Products', 'Upload File with Products']}
              onUpdate={category => {
                setSelectedType(category === NUMBERS.Zero ? Constants.CategoryProducts : Constants.UploadedProducts)
              }}
              reference="radio button"
              selected={false}
              selectedItemIndex={selectedType === Constants.CategoryProducts ? 0 : 1}
              size="small"
              className="mr-8"
              id={'uploadedProducts'}
            />
            <div>
              <Toggle
                uiSchema={{
                  label: 'Ignore SKU List',
                  isDark: true,
                  size: 'medium',
                  className: 'pre-reqsite-toggle mr-3',
                  variant: 'horizontal',
                  showCheckedIcon: false,
                }}
                style={{ fontSize: '18px' }}
                checked={ignoreSkuList}
                value={ignoreSkuList}
                className="text-7xl"
                size="medium"
                variant="horizontal"
                updateValue={val => handleIgnoreSkuChange(val, 'ignoreSku')}
              />
            </div>

          </div>
          <div className='flex justify-between items-center pt-4'>
            <InfoOrError info={"Products are not preselected. Select or upload products to include."} className="text-xs text-gray-150"></InfoOrError>
          </div>

          <hr className="products-hr" />
          {selectedType === Constants.CategoryProducts && (
            <CategoryWrapper
              selectedCategoryInitialState={selectedCategoryInitialState}
              key={selectedType}
              categories={categoryListData}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              isAllCategories={isAllCategories}
            />
          )}
          {selectedType === Constants.UploadedProducts && (
            <FileUploadWithoutModal
              activeTab={name}
              key={selectedType}
              handleDownloadTemplate={handleFileDownload}
              handleViewTable={() => { }}
              description={uploadFileDescriptionForCSV()}
              headerTitle={Constants.UploadProducts}
              acceptedExtensions={Constants.CSV}
              files={mediaFile?.length > 0 ? mediaFile : []}
              onUpload={file => {
                dispatch(setProductCsvFile({ activeTab: name, mediaFile: [file?.name] }))
                setUploadError(false)
              }}
              feedbackType={uploadError ? Constants.Error : Constants.Info}
              helperText={uploadError ? 'Upload Error: Please use only .CSV file type' : ''}
              onUploadError={error => {
                if (error) {
                  setUploadError(true)
                }
              }}
              onUploadDelete={handleDelete}
              variant={mediaFile?.length > 0 ? Constants.Complete : Constants.Edit}
              type={Constants.Single}
              className="!max-w-full"
              containerClassName="!w-full"
              isCsvValidationRequired={true}
              required={targetType !== TARGET_TYPES.MainTarget}
              showDownloadTemplate={true}
              isValidateProductIds={true}
              productIdsFilename={mediaFile?.[0]}
              onValidatedFileName={fileName => dispatch(setProductCsvFile({ activeTab: name, mediaFile: [fileName] }))}
            />
          )}
        </div>
      </PageContainer>
      {selectedType === Constants.CategoryProducts && (
        <ProductsTable
          onUpdate={onUpdate}
          isUploadTable={false}
          columns={cellDataMappedColsLocal}
          records={localProductData}
          rowSelection={getEffectiveSelectedRows()}
          setRowSelection={setRowSelection}
          handleBulkAction={handleBulkAction}
          setPageIndex={setPageIndex}
          setSortingObj={setSortingObj}
          setSearchArry={setSearchArry}
          handleResetAll={handleResetAll}
          productListData={productListData}
          pageIndex={pageIndex}
          isLoading={isLoading}
        />
      )}
      {uploadComplete && (
        <ProductsTable isUploadTable={true} rowSelection={rowSelection} setRowSelection={setRowSelection} />
      )}
    </>
  )
}

export default SalesTargetProducts
